/* Jolisina sections (jl-*) — small vanilla web components. Safe to include several times. */
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Horizontal scroll carousel with optional arrows + progress bar */
  define('jl-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.jl-track');
      if (!this.track) return;
      this.prev = this.querySelector('.jl-nav--prev');
      this.next = this.querySelector('.jl-nav--next');
      this.bar = this.querySelector('.jl-progress');
      const step = (dir) => {
        const item = this.track.firstElementChild;
        const w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
        const per = Math.max(1, Math.round(this.track.clientWidth / w));
        this.track.scrollBy({ left: dir * w * per * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' });
      };
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      requestAnimationFrame(this.update);
    }
    disconnectedCallback() { window.removeEventListener('resize', this.update); }
    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const pos = Math.abs(t.scrollLeft);
      if (this.prev) this.prev.disabled = pos <= 2;
      if (this.next) this.next.disabled = pos >= max - 2;
      if (this.bar) {
        const ratio = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
        this.bar.hidden = ratio >= 0.999;
        const w = Math.max(ratio * 100, 8);
        this.bar.style.setProperty('--jl-progress-w', w + '%');
        this.bar.style.setProperty('--jl-progress-x', (max > 0 ? (pos / max) * ((100 - w) / w) * 100 : 0) + '%');
      }
    }
  });

  /* Marquee: clones the content until it is at least twice as wide as the viewport */
  define('jl-marquee', class extends HTMLElement {
    connectedCallback() {
      const inner = this.querySelector('.jl-marquee__inner');
      if (!inner || inner.dataset.ready) return;
      const unit = Array.from(inner.children);
      if (!unit.length) return;
      const fill = () => {
        let guard = 0;
        while (inner.scrollWidth < this.clientWidth * 2 && guard++ < 30) unit.forEach((n) => inner.appendChild(n.cloneNode(true)));
        const copy = Array.from(inner.children).map((n) => n.cloneNode(true));
        copy.forEach((n) => { n.setAttribute('aria-hidden', 'true'); inner.appendChild(n); });
        const speed = parseFloat(this.dataset.speed || 15); // 1 (slow) .. 50 (fast) like the original setting
        const pxPerSec = 20 + speed * 6;
        inner.style.setProperty('--jl-marquee-duration', (inner.scrollWidth / 2 / pxPerSec) + 's');
        inner.dataset.ready = '1';
      };
      requestAnimationFrame(fill);
      if (this.dataset.mode === 'scroll') {
        inner.style.animation = 'none';
        let last = window.scrollY, x = 0;
        window.addEventListener('scroll', () => {
          const d = window.scrollY - last; last = window.scrollY;
          const half = inner.scrollWidth / 2 || 1;
          x = (x - d * 0.6) % half; if (x > 0) x -= half;
          inner.style.transform = `translateX(${x}px)`;
        }, { passive: true });
      }
    }
  });

  /* Slideshow: fade / slide, dots or arrows, autoplay */
  define('jl-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.jl-slide'));
      this.dots = Array.from(this.querySelectorAll('.jl-dot'));
      this.index = 0;
      if (this.slides.length < 2) return;
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
      this.querySelector('.jl-ss-prev')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('.jl-ss-next')?.addEventListener('click', () => this.go(this.index + 1, true));
      let sx = null;
      this.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx; sx = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
      });
      this.play();
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.stop(); this.go(i); }
      });
    }
    go(i, user) {
      const n = this.slides.length;
      i = (i + n) % n;
      this.slides.forEach((s, k) => { s.classList.toggle('is-active', k === i); s.setAttribute('aria-hidden', k === i ? 'false' : 'true'); });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      this.index = i;
      if (user) this.play();
    }
    play() {
      this.stop();
      if (this.dataset.autoplay !== 'true') return;
      this.timer = setInterval(() => this.go(this.index + 1), (parseFloat(this.dataset.speed) || 6) * 1000);
    }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); }
  });

  /* Images with text scrolling: highlights the image that matches the text item in view */
  define('jl-scroll-images', class extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.jl-sit__item'));
      this.images = Array.from(this.querySelectorAll('.jl-sit__visual .jl-sit__img'));
      this.counter = this.querySelector('.jl-sit__current');
      if (!this.items.length) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const i = this.items.indexOf(e.target);
          this.items.forEach((it, k) => it.classList.toggle('is-active', k === i));
          this.images.forEach((im, k) => im.classList.toggle('is-active', k === i));
          if (this.counter) this.counter.textContent = String(i + 1).padStart(2, '0');
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      this.items.forEach((it) => io.observe(it));
    }
  });

  /* Hot spots: toggle popovers, close on outside click / Escape */
  define('jl-hotspots', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.jl-hs__dot');
        if (btn) {
          const spot = btn.closest('.jl-hs__spot');
          const open = !spot.classList.contains('is-open');
          this.closeAll();
          spot.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          return;
        }
        if (e.target.closest('.jl-hs__close')) this.closeAll();
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
    }
    closeAll() {
      this.querySelectorAll('.jl-hs__spot.is-open').forEach((s) => {
        s.classList.remove('is-open');
        s.querySelector('.jl-hs__dot')?.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* Multiple images with text: stacked images + text, cycles through items */
  define('jl-multi-images', class extends HTMLElement {
    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.jl-mit__image'));
      this.texts = Array.from(this.querySelectorAll('.jl-mit__text'));
      this.dots = Array.from(this.querySelectorAll('.jl-mit__dot'));
      this.index = 0;
      if (this.images.length < 2) return;
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      this.querySelector('.jl-mit__prev')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.jl-mit__next')?.addEventListener('click', () => this.go(this.index + 1));
      this.images.forEach((im, i) => im.addEventListener('click', () => this.go(i)));
    }
    go(i) {
      const n = this.images.length;
      i = (i + n) % n;
      this.index = i;
      this.images.forEach((im, k) => { im.classList.toggle('is-active', k === i); im.style.zIndex = k === i ? n + 1 : n - ((k - i + n) % n); });
      this.texts.forEach((t, k) => { t.hidden = k !== i; });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    }
  });

  /* Recently viewed products: handles from localStorage, cards rendered by the static section jl-product-card */
  define('jl-recently-viewed', class extends HTMLElement {
    async connectedCallback() {
      const limit = parseInt(this.dataset.limit || '12', 10);
      const current = this.dataset.current || '';
      let handles = [];
      ['jl_recently_viewed', 'almond_recently_viewed'].forEach((key) => {
        try { (JSON.parse(localStorage.getItem(key) || '[]') || []).forEach((h) => { if (typeof h === 'string' && !handles.includes(h)) handles.push(h); }); } catch (e) { /* storage unavailable */ }
      });
      handles = handles.filter((h) => h && h !== current).slice(0, limit);
      if (!handles.length) return;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const cards = await Promise.all(handles.map(async (h) => {
        try {
          const r = await fetch(`${root}products/${encodeURIComponent(h)}?section_id=jl-product-card`);
          if (!r.ok) return '';
          const html = await r.text();
          const d = document.createElement('div');
          d.innerHTML = html;
          const card = d.querySelector('.jl-product-card-render');
          return card ? card.innerHTML : '';
        } catch (e) { return ''; }
      }));
      const track = this.querySelector('.jl-products');
      cards.filter(Boolean).forEach((html) => {
        const w = document.createElement('div');
        w.className = 'jl-products__item';
        w.innerHTML = html;
        track.appendChild(w);
      });
      if (track.children.length) {
        this.hidden = false;
        if (window.theme && typeof theme.manuallyLoadImages === 'function') theme.manuallyLoadImages(this);
        this.querySelector('jl-carousel')?.update?.();
      }
    }
  });

  /* Tracker: call on product pages with the current handle */
  window.jlTrackRecentlyViewed = (handle) => {
    if (!handle) return;
    try {
      const list = JSON.parse(localStorage.getItem('jl_recently_viewed') || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem('jl_recently_viewed', JSON.stringify(list.slice(0, 24)));
    } catch (e) { /* storage unavailable */ }
  };
})();
